import type { Source } from '@eformulieren/model'
import CSL from 'citeproc'
import type { CiteprocCitation, CiteprocItem, Engine, Sys } from 'citeproc'
import { HARVARD_CSL } from './assets/harvardCsl.js'
import { LOCALE_AND_GB } from './assets/localeAndGb.js'
import type { CitationCluster, CitationResult, ReferenceEntry } from './types.js'

/**
 * De citatie-engine: citeproc-js, met Harvard volgens Cite Them Right hard gecodeerd (ontwerp
 * bijlage C.2 en C.3, naar `CitatenPOC/src/citation/engine.ts`).
 *
 * **Dezelfde stijl als de CitatenPOC.** De POC rekent standaard met `harvard-cite-them-right` en de
 * en-GB-locale (`DEFAULT_STYLE_ID = 'harvard'` in `CitatenPOC/src/citation/styles.ts`), en de
 * controls volgen de POC (eisenboek 12.9, besluit van 30 oktober 2026). Tot dat besluit rekende dit
 * product met APA. Vorm, volgorde, et-al-regel en de letters a en b komen daarmee uit het
 * CSL-bestand van de POC, niet uit eigen instellingen.
 *
 * **Eén stijl, geen instelling.** De POC laat de invuller een stijl kiezen (Harvard, APA, IEEE,
 * Vancouver) en een sjabloon er een vastzetten. Dit product niet: alleen Harvard (Cite Them Right),
 * geen keuze per lezer of per definitie (besluit van 31 oktober 2026, eisenboek 12.23, ontwerp C.3).
 *
 * **De stijl en de locale zijn string-constanten en geen `?raw`-import.** Dit pakket compileert met
 * `tsc -b` naar `dist`, dat geen niet-TypeScript-bestanden meeneemt; zie de kop van
 * `assets/harvardCsl.ts`.
 */
export interface CitationEngine {
  process(clusters: readonly CitationCluster[]): CitationResult
}

/**
 * Eén citeproc-engine voor de hele module, met Harvard en en-GB ingelezen, en per berekening opnieuw
 * gevuld.
 *
 * **Waarom gedeeld.** De engine bouwen (stijl en locale inlezen) kost gemeten zo'n 165 ms in Node op
 * een ontwikkelmachine en rond 2 s op de CI-machine; een berekening daarna een paar milliseconden
 * (`engine.node.test.ts`, "de gedeelde engine"). Met een engine per aanroep betaalde elke
 * herberekening, elke oudere stand en elke overdracht dat opnieuw.
 *
 * **Waarom dat veilig is.** Een berekening is synchroon: `process` zet de bronnenlezer van de
 * aanroeper, en `rebuildProcessorState` bouwt de hele toestand uit de citaties van die ene
 * berekening op, met de bronnen zoals ze nu zijn. Een letter of een titel van een vorige berekening
 * komt niet mee (gemeten in dezelfde test). Gooit citeproc, dan wordt de engine weggegooid en de
 * volgende keer opnieuw gebouwd, zodat een halve toestand nooit blijft hangen.
 */
let shared: Engine | undefined
let reading: () => readonly Source[] = () => []

function sharedEngine(): Engine {
  if (shared !== undefined) return shared
  const sys: Sys = {
    retrieveLocale: () => LOCALE_AND_GB,
    retrieveItem(id: string): CiteprocItem {
      const source = reading().find((candidate) => candidate.id === id)
      if (source === undefined) throw new Error(`bron niet gevonden in de bronnenopslag: ${id}`)
      // Een kloon: citeproc verrijkt items ter plekke, en de bronnenopslag blijft de onaangeroerde waarheid.
      return structuredClone(source) as unknown as CiteprocItem
    },
  }
  shared = new CSL.Engine(sys, HARVARD_CSL, 'en-GB', true)
  return shared
}

/**
 * Bouwt de gedeelde engine alvast, als dat nog niet is gebeurd.
 *
 * Voor een test die meet wat er na een wijziging gebeurt en niet hoe lang citeproc koud opstart:
 * elk testbestand in de browser laadt deze module opnieuw, en de eerste bouw kost op de CI-machine
 * rond 2 s, ruim boven de 1 s die `waitFor` standaard wacht (gemeten met een kunstmatig trage bouw
 * in `useCitations.test.ts` en `useSectionCitations.test.ts`).
 */
export function warmCitationEngine(): void {
  sharedEngine()
}

/**
 * Een citatie-engine over de bronnen die `readSources` levert.
 *
 * `readSources` wordt bij elke `process`-aanroep opnieuw geraadpleegd: een bron die net is
 * toegevoegd werkt zonder de engine opnieuw te bouwen. De citeproc-engine eronder is gedeeld (zie
 * hierboven), dus een nieuwe `createCitationEngine` kost niets.
 */
export function createCitationEngine(readSources: () => readonly Source[]): CitationEngine {
  function process(clusters: readonly CitationCluster[]): CitationResult {
    const current = readSources()
    const sourceIds = new Set(current.map((source) => source.id))

    // Defensief, naar CitatenPOC: een cluster dat naar een onbekende of verwijderde bron
    // wijst laat `rebuildProcessorState` gooien en zou de héle uitkomst legen. Een dubbele
    // `citatieId` (kopiëren/plakken van een citatie-node) doet hetzelfde; de eerste wint,
    // en beide nodes delen toch dezelfde `citatieId` en dus hetzelfde in-tekst-label.
    const seenIds = new Set<string>()
    const safe = clusters
      .map((cluster) => ({
        citatieId: cluster.citatieId,
        bronnen: cluster.bronnen.filter((id) => sourceIds.has(id)),
      }))
      .filter((cluster) => cluster.bronnen.length > 0)
      .filter((cluster) => {
        if (seenIds.has(cluster.citatieId)) return false
        seenIds.add(cluster.citatieId)
        return true
      })

    const citations: CiteprocCitation[] = safe.map((cluster) => ({
      citationID: cluster.citatieId,
      citationItems: cluster.bronnen.map((id) => ({ id })),
      properties: { noteIndex: 0 },
    }))

    try {
      reading = () => current
      const engine = sharedEngine()
      // Eerst het register leeg: citeproc onthoudt per bron-id wat hij las, ook de namen waarop hij
      // disambigueert. Zonder dit gaf een eerdere bron met hetzelfde id en een andere auteur in de
      // podsuite geen a en b meer (`handover.test.ts`, "nummeren over de secties van de laag").
      engine.updateItems([])
      const inText = new Map<string, string>()
      for (const [citationID, , html] of engine.rebuildProcessorState(citations, 'html')) {
        inText.set(citationID, html)
      }

      const [bibliographyMeta, rules] = engine.makeBibliography()
      const referenceList: ReferenceEntry[] = rules.map((html, i) => ({
        bronId: (bibliographyMeta.entry_ids[i]?.[0] ?? '') as Source['id'],
        html,
      }))

      return { inTekst: inText, literatuurlijst: referenceList }
    } catch (error) {
      // citeproc gooide alsnog (bijvoorbeeld op een ongebruikelijke brondatacombinatie).
      // Een lege-maar-geldige uitkomst houdt het veld en de lijst in beeld in plaats van de
      // hele render te laten omvallen; de fout gaat naar het log (`docs/eformulieren-ontwerp.md`,
      // "log de volledige foutketen").
      shared = undefined
      console.error('citatie-engine faalde; lege uitkomst teruggegeven', error)
      return { inTekst: new Map(), literatuurlijst: [] }
    }
  }

  return { process }
}
